/* eslint-disable no-use-before-define */
import { FC, Fragment, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Breadcrumbs, Button, Group, Table, Token, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';
import { differenceWith, isEqual } from 'lodash';

import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import {
  Brand,
  ContentParametersDefaultSource,
  ContentParametersMapping,
  useUpdateConfigDefaultParametersMutation,
  useUpdatePublicationBrandDefaultParametersMutation
} from 'data/generated/graphql';
import {
  AccordionTitleWithUndo,
  ContentParametersError,
  LoadingContentParameters
} from 'features/content-parameter-settings/components';
import { isValidRange, validate } from 'features/content-parameter-settings/utils';
import { useConvertedProperty } from 'hooks';
import useContentParametersCatalog from 'hooks/contentParametersMapping/useContentParametersCatalog';
import { MappingProvider } from '../content-parameter-settings/MappingContext';
import { ContentParametersEditor } from '../content-parameter-settings/MappingView';
import { useMappingState } from '../content-parameter-settings/useMappingState';

const BRAND_DEFAULT_ROW_ID = '__BRAND_DEFAULT__';
const BRAND_DEFAULT_ROW_LABEL = 'Brand default parameters';

const defaultSourceToken: Record<ContentParametersDefaultSource, { color: 'emerald' | 'ice' | 'gray'; label: string }> =
  {
    [ContentParametersDefaultSource.Publication]: { color: 'emerald', label: 'Publication default' },
    [ContentParametersDefaultSource.BrandDefault]: { color: 'ice', label: 'Inherited from brand default' },
    [ContentParametersDefaultSource.None]: { color: 'gray', label: 'No default' }
  };

const ContentParameterConfigDefaults: FC = () => {
  const { user } = useAuth();
  const currentProperty = useConvertedProperty();
  const { data, error } = useContentParametersCatalog();
  const perPubMutation = useUpdateConfigDefaultParametersMutation();
  const brandDefaultMutation = useUpdatePublicationBrandDefaultParametersMutation();
  const { alertError, alertSuccess } = useAlert();
  const queryClient = useQueryClient();

  const { mappingState, dispatch } = useMappingState([]);

  useEffect(() => {
    if (data) {
      const { treatments, brandDefaultParameters } = data.getResolvedContentParametersCatalog;

      const brandDefaultRow: ContentParametersMapping = {
        allessehId: '',
        treatmentType: BRAND_DEFAULT_ROW_ID,
        createdUtc: 0,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        creatorUser: { djUserId: user?.dj_user_id ?? '' },
        publicationKey: currentProperty as Brand,
        parameters: brandDefaultParameters,
        deprecated: false,
        comment: '',
        label: BRAND_DEFAULT_ROW_LABEL
      };

      const treatmentRows = treatments.map<ContentParametersMapping>(
        ({ treatmentType, label, defaultParameters }) => ({
          allessehId: '',
          treatmentType,
          createdUtc: 0,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          creatorUser: { djUserId: user?.dj_user_id ?? '' },
          publicationKey: currentProperty as Brand,
          parameters: defaultParameters,
          deprecated: false,
          comment: '',
          label
        })
      );

      dispatch({
        type: 'SET_MAPPINGS',
        payload: {
          mappings: [brandDefaultRow, ...treatmentRows]
        }
      });
    }
  }, [currentProperty, data, dispatch, user?.dj_user_id]);

  const isSaving = perPubMutation.isLoading || brandDefaultMutation.isLoading;

  const handleSaveChanges = () => {
    if (!currentProperty) {
      alertError('No publication selected.');
      return;
    }

    const changedMappings = differenceWith(mappingState.modifiedMappings, mappingState.originalMappings, isEqual);
    if (changedMappings.length === 0) {
      alertError('No changes to save.');
      return;
    }

    const mappingWithError = changedMappings.find((changedMapping) => {
      const isValidSchema = validate(changedMapping.parameters);
      const errors = isValidRange(changedMapping.parameters);
      return errors.length > 0 || !isValidSchema;
    });

    if (mappingWithError) {
      const isValidSchema = validate(mappingWithError.parameters);
      const errorObject = {
        treatmentType: mappingWithError.treatmentType,
        errors: isValidRange(mappingWithError.parameters),
        schemaErrors: validate.errors,
        isValidSchema
      };

      const schemaErrorMessages =
        errorObject.schemaErrors?.map(
          (error) => `${error.instancePath}${error.instancePath ? ': ' : ''}${error.message}`
        ) ?? [];

      const errorString = [...errorObject.errors, ...schemaErrorMessages].join('; ');
      const rowLabel =
        mappingWithError.treatmentType === BRAND_DEFAULT_ROW_ID
          ? BRAND_DEFAULT_ROW_LABEL
          : `${mappingWithError.treatmentType} treatment type`;
      alertError(`There are errors in the ${rowLabel}: ${errorString}`, {
        wrapNewlines: false,
        autoDismiss: false
      });

      return;
    }

    const changedBrandDefault = changedMappings.find((m) => m.treatmentType === BRAND_DEFAULT_ROW_ID);
    const changedTreatmentMappings = changedMappings.filter((m) => m.treatmentType !== BRAND_DEFAULT_ROW_ID);

    const applyMappings = (mappings: typeof changedMappings) => {
      mappings.forEach((changedMapping) => {
        dispatch({
          type: 'APPLY_MODIFIED_MAPPING',
          payload: { treatmentType: changedMapping.treatmentType }
        });
        dispatch({
          type: 'UNDO_CHANGES',
          payload: { treatmentType: changedMapping.treatmentType }
        });
      });
    };

    const errorMessage = (err: unknown) => (err instanceof Error ? err.message : 'unknown error');

    const runMutations = async () => {
      const successes: string[] = [];
      const errors: string[] = [];

      if (changedBrandDefault) {
        try {
          await brandDefaultMutation.mutateAsync({
            publicationKey: currentProperty,
            contentParametersBrandDefaultParametersUpdateInput: { parameters: changedBrandDefault.parameters }
          });
          applyMappings([changedBrandDefault]);
          successes.push('Brand default parameters');
        } catch (mutationError) {
          errors.push(`brand default parameters: ${errorMessage(mutationError)}`);
        }
      }

      if (changedTreatmentMappings.length > 0) {
        try {
          await perPubMutation.mutateAsync({
            publicationKey: currentProperty,
            contentParametersConfigDefaultsUpdateInput: {
              defaultParametersByTreatment: changedTreatmentMappings.map((mapping) => ({
                treatmentType: mapping.treatmentType,
                parameters: mapping.parameters
              }))
            }
          });
          applyMappings(changedTreatmentMappings);
          successes.push('CONFIG default parameters');
        } catch (mutationError) {
          errors.push(`CONFIG default parameters: ${errorMessage(mutationError)}`);
        }
      }

      if (successes.length > 0) {
        void queryClient.invalidateQueries(['ResolvedContentParametersCatalog']);
        alertSuccess(`${successes.join(' and ')} updated successfully.`);
      }
      if (errors.length > 0) {
        alertError(`Error updating ${errors.join('; ')}.`);
      }
    };

    void runMutations();
  };

  if (data) {
    const changedMappings = differenceWith(mappingState.modifiedMappings, mappingState.originalMappings, isEqual);
    const hasChanges = changedMappings.length > 0;
    const { treatments } = data.getResolvedContentParametersCatalog;
    const defaultSourceByTreatment = new Map(treatments.map((t) => [t.treatmentType, t.defaultSource]));

    return (
      <>
        <Breadcrumbs margin={{ bottom: 'lg' }}>
          <Breadcrumbs.Item componentEl={Link} to={`/${currentProperty}/site-config/mobile-and-app`}>
            {currentProperty?.toUpperCase()}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item
            componentEl={Link}
            to={`/${currentProperty}/site-config/mobile-and-app/content-presentation/config-defaults`}
          >
            Default Parameters
          </Breadcrumbs.Item>
        </Breadcrumbs>
        <Group margin={{ bottom: 'lg' }} valign="baseline" align="end">
          <Button primary disabled={!hasChanges || isSaving} onClick={handleSaveChanges}>
            {isSaving ? 'Saving changes...' : 'Save Changes'}
          </Button>
        </Group>
        <Group margin={{ bottom: 'lg' }}>
          <Typography align="justify">
            This is the editing interface for default content presentation parameters per treatment type. These defaults
            are used when creating new sections or when a treatment type does not have specific parameters configured.
          </Typography>
          <Typography align="justify">
            The <em>Brand default parameters</em> row at the top is a brand-wide fallback: any treatment type without its
            own per-treatment default inherits from it. Per-treatment rows override the brand default.
          </Typography>
        </Group>
        <Table fullWidth>
          <Table.Body>
            <Table.Row>
              <Table.Cell align="center" valign="middle">
                <strong>Treatment Type</strong>
              </Table.Cell>
              <Table.Cell align="center" valign="middle">
                <strong>Key</strong>
              </Table.Cell>
              <Table.Cell align="center" valign="middle">
                <strong>Source</strong>
              </Table.Cell>
            </Table.Row>
            {mappingState.modifiedMappings.map((mapping, index) => {
              const isModifiedMapping = changedMappings.some(
                (changedMapping) => changedMapping.treatmentType === mapping.treatmentType
              );
              const isBrandDefaultRow = mapping.treatmentType === BRAND_DEFAULT_ROW_ID;
              const source = isBrandDefaultRow
                ? null
                : defaultSourceByTreatment.get(mapping.treatmentType) ?? ContentParametersDefaultSource.None;
              const token = source ? defaultSourceToken[source] : null;

              return (
                <Fragment key={mapping.treatmentType}>
                  <Table.Row>
                    <Table.Cell align="center" valign="middle">
                      {mapping.label}
                    </Table.Cell>
                    <Table.Cell align="center" valign="middle">
                      {isBrandDefaultRow ? '—' : mapping.treatmentType}
                    </Table.Cell>
                    <Table.Cell align="center" valign="middle">
                      {token ? <Token color={token.color}>{token.label}</Token> : <Token color="ice">Brand default</Token>}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan="3">
                      <MappingProvider
                        mapping={mapping}
                        dispatch={dispatch}
                        originalMapping={mappingState.originalMappings[index]}
                      >
                        <Group.Item fullWidth>
                          <Accordion TitleComponent={<AccordionTitleWithUndo mappingIsModified={isModifiedMapping} />}>
                            <Group direction="column" gap="mlg">
                              <ContentParametersEditor>
                                <ContentParametersEditor.JSONEditor
                                  dispatch={dispatch}
                                  treatmentType={mapping.treatmentType}
                                  parameters={mapping.parameters}
                                  disabled={false}
                                />
                              </ContentParametersEditor>
                            </Group>
                          </Accordion>
                        </Group.Item>
                      </MappingProvider>
                    </Table.Cell>
                  </Table.Row>
                </Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </>
    );
  }

  if (error) {
    return <ContentParametersError error={error} />;
  }

  return <LoadingContentParameters>Loading CONFIG defaults...</LoadingContentParameters>;
};

export default ContentParameterConfigDefaults;
